import {
  CurrencyCode,
  enumCurrencyCode,
  enumUnit,
  Money,
  PurchaseLineItemType,
  Unit,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { WeightUnitType } from '@rematter/modules/trade/types/load';
import { toMoney } from '@rematter/modules/trade/utils/money';
import { toWeight, zeroWeight } from '@rematter/modules/trade/utils/weight';
import { weightToPounds } from '@rematter/ui-web';
import { atom } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import {
  EntriesCallback,
  enumLineItemKind,
  ItemsInterfaceState,
  ItemsListEntry,
  NormalizedDeduction,
  NormalizedFreightCharge,
  NormalizedLineItem,
  WeightField,
} from './_types';

import { $ticketDirection } from '~/modules/trade/_state/scaleTicketPro.state';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { convertWeightForPricing } from '~/modules/trade/utils/weight';

// 🔘🔘🔘 PLAIN ATOMS
export const $footerFocusedField = atom<WeightField | null>(null);

export const $itemsInterfaceDisabled = atom(false);
export const $itemsInterfaceInventoryLocked = atom(false);
export const $itemsInterfaceShowPricing = atom(false);
export const $itemsInterfaceFacilityDefaultUnit = atom<Unit>(enumUnit.Pounds);
export const $itemsInterfaceWeightUnitMode = atom<WeightUnitType>('scale');

export const $itemsInterfaceEntries = atom<ItemsInterfaceState>({
  items: [],
  heavyWeight: null,
  lightWeight: null,
});
export const $itemsInterfaceCallback = atom<EntriesCallback | null>(null);
export const $newlyAddedItemId = atom<string | null>(null);
export const $itemsInterfaceErrorItemIds = atom<Set<string>>(new Set<string>());

export type DeductionChangeAction = 'create' | 'update' | 'delete';
export type DeductionChangeCallbackFn = (
  action: DeductionChangeAction,
  deduction: NormalizedDeduction,
  allDeductions: NormalizedDeduction[],
) => void;
export const $deductionChangeCallback = atom<{
  fn: DeductionChangeCallbackFn;
} | null>(null);

export const $deductionsTotalFromServer = atom<number>(0);

export const $itemsInterfaceTicketId = atom<string | null>(null);
export const $itemsInterfaceTicketStatus = atom<string | null>(null);

export const $itemsInterfacePricingLocked = atom((get) => {
  const status = get($itemsInterfaceTicketStatus);
  return status === 'PriceComplete' || status === 'Paid';
});

export type SuggestedItemPrice = { unitPrice: Money; pricingUnit: Unit };

export const resolveSuggestedPrice = (
  suggestedPrices: Map<string, SuggestedItemPrice>,
  itemId: string | null | undefined,
): { unitPrice: Money; pricingUnit: Unit } | undefined => {
  if (!itemId) return undefined;
  return suggestedPrices.get(itemId);
};

const EMPTY_HIGHLIGHTED_IDS: string[] = [];
const EMPTY_SUGGESTED_PRICES: Map<string, SuggestedItemPrice> = new Map();

export const $itemsInterfaceHighlightedMaterialIds = atom<string[]>(
  EMPTY_HIGHLIGHTED_IDS,
);
export const $itemsInterfaceHighlightedSectionTitle = atom<string | undefined>(
  undefined,
);
export const $itemsInterfaceSuggestedPrices = atom<
  Map<string, SuggestedItemPrice>
>(EMPTY_SUGGESTED_PRICES);

// 🟠🟠🟠 ACTION -> RESET TICKET ID (unmount cleanup)
export const $resetItemsInterfaceTicketId = atom(null, (_get, set) => {
  set($itemsInterfaceTicketId, null);
});

// 🟢🟢🟢 HELPER -> internal callback notifier
const notifyCallback = (
  get: (a: typeof $itemsInterfaceCallback) => EntriesCallback | null,
  state: ItemsInterfaceState,
) => {
  const callback = get($itemsInterfaceCallback);
  callback?.fn(state.items);
};

const notifyDeductionChange = (
  get: (
    a: typeof $deductionChangeCallback,
  ) => { fn: DeductionChangeCallbackFn } | null,
  action: DeductionChangeAction,
  deduction: NormalizedDeduction,
  allItems: ItemsListEntry[],
) => {
  const cb = get($deductionChangeCallback);
  if (!cb) return;
  const allDeductions = allItems.filter(
    (e): e is NormalizedDeduction => e.kind === enumLineItemKind.Deduction,
  );
  cb.fn(action, deduction, allDeductions);
};

// 🟢🟢🟢 HELPER -> ItemsInterfaceConfig type
export type ItemsInterfaceConfig = {
  entries: ItemsListEntry[];
  showPricing: boolean;
  disabled: boolean;
  inventoryLocked: boolean;
  facilityDefaultUnit: Unit;
  weightUnitMode: WeightUnitType;
  ticketId: string | null;
  ticketStatus: string | null;
  heavyWeight?: Weight | null;
  lightWeight?: Weight | null;
  onUpdateEntries?: (entries: ItemsListEntry[]) => void;
  onDeductionChange?: DeductionChangeCallbackFn;
  highlightedMaterialIds?: string[];
  highlightedSectionTitle?: string;
  suggestedPrices?: Map<string, SuggestedItemPrice>;
};

// 🟠🟠🟠 ACTION -> SYNC CONFIG
export const $syncItemsInterfaceConfig = atom(
  null,
  (get, set, config: ItemsInterfaceConfig) => {
    const prevTicketId = get($itemsInterfaceTicketId);
    const prevEntries = get($itemsInterfaceEntries);
    const ticketChanged = prevTicketId !== config.ticketId;

    const nextHeavyWeight = ticketChanged
      ? (config.heavyWeight ?? null)
      : prevEntries.heavyWeight;
    const nextLightWeight = ticketChanged
      ? (config.lightWeight ?? null)
      : prevEntries.lightWeight;

    if (
      config.entries !== prevEntries.items ||
      nextHeavyWeight !== prevEntries.heavyWeight ||
      nextLightWeight !== prevEntries.lightWeight
    ) {
      set($itemsInterfaceEntries, {
        items: config.entries,
        heavyWeight: nextHeavyWeight,
        lightWeight: nextLightWeight,
      });
    }

    if (ticketChanged) {
      set($itemsInterfaceErrorItemIds, new Set());
    }

    if (config.onUpdateEntries) {
      set($itemsInterfaceCallback, { fn: config.onUpdateEntries });
    } else {
      set($itemsInterfaceCallback, null);
    }

    if (config.onDeductionChange) {
      set($deductionChangeCallback, { fn: config.onDeductionChange });
    } else {
      set($deductionChangeCallback, null);
    }

    const deductions = config.entries.filter(
      (e): e is NormalizedDeduction => e.kind === enumLineItemKind.Deduction,
    );
    const deductionsTotal = deductions.reduce(
      (sum, d) => sum + (d.calculatedAmount?.amount ?? 0),
      0,
    );
    set($deductionsTotalFromServer, deductionsTotal);

    if (get($itemsInterfaceDisabled) !== config.disabled)
      set($itemsInterfaceDisabled, config.disabled);
    if (get($itemsInterfaceShowPricing) !== config.showPricing)
      set($itemsInterfaceShowPricing, config.showPricing);
    if (get($itemsInterfaceInventoryLocked) !== config.inventoryLocked)
      set($itemsInterfaceInventoryLocked, config.inventoryLocked);
    if (get($itemsInterfaceFacilityDefaultUnit) !== config.facilityDefaultUnit)
      set($itemsInterfaceFacilityDefaultUnit, config.facilityDefaultUnit);
    if (get($itemsInterfaceWeightUnitMode) !== config.weightUnitMode)
      set($itemsInterfaceWeightUnitMode, config.weightUnitMode);
    if (get($itemsInterfaceTicketId) !== config.ticketId)
      set($itemsInterfaceTicketId, config.ticketId);
    if (get($itemsInterfaceTicketStatus) !== config.ticketStatus)
      set($itemsInterfaceTicketStatus, config.ticketStatus);

    const newHighlighted =
      config.highlightedMaterialIds ?? EMPTY_HIGHLIGHTED_IDS;
    if (get($itemsInterfaceHighlightedMaterialIds) !== newHighlighted)
      set($itemsInterfaceHighlightedMaterialIds, newHighlighted);
    if (
      get($itemsInterfaceHighlightedSectionTitle) !==
      config.highlightedSectionTitle
    )
      set(
        $itemsInterfaceHighlightedSectionTitle,
        config.highlightedSectionTitle,
      );
    const newPrices = config.suggestedPrices ?? EMPTY_SUGGESTED_PRICES;
    if (get($itemsInterfaceSuggestedPrices) !== newPrices)
      set($itemsInterfaceSuggestedPrices, newPrices);
  },
);

// 🟠🟠🟠 ACTION -> UPDATE HEAVY WEIGHT
export const $updateHeavyWeight = atom(
  null,
  (get, set, weight: Weight | null) => {
    const state = get($itemsInterfaceEntries);
    const updated = { ...state, heavyWeight: weight };
    set($itemsInterfaceEntries, updated);
    notifyCallback(get, updated);
  },
);

// 🟠🟠🟠 ACTION -> UPDATE LIGHT WEIGHT
export const $updateLightWeight = atom(
  null,
  (get, set, weight: Weight | null) => {
    const state = get($itemsInterfaceEntries);
    const updated = { ...state, lightWeight: weight };
    set($itemsInterfaceEntries, updated);
    notifyCallback(get, updated);
  },
);

// 🟠🟠🟠 ACTION -> ADD LINE ITEM
export const $addLineItem = atom(
  null,
  (
    get,
    set,
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => {
    const state = get($itemsInterfaceEntries);

    const newId = data?.id ?? `temp-${uuidv4()}`;
    const unit = data?.pricingUnit ?? enumUnit.Pounds;

    const newItem: NormalizedLineItem = {
      kind: enumLineItemKind.LineItem,
      id: newId,
      type,
      itemId: data?.itemId ?? null,
      tagId: data?.tagId ?? null,
      weights: data?.weights ?? null,
      unitPrice: data?.unitPrice ?? null,
      pricingUnit: unit,
      notes: data?.notes,
      sourceLineId: data?.sourceLineId,
      vehicleVin: data?.vehicleVin,
      vehicleYear: data?.vehicleYear,
      vehicleMake: data?.vehicleMake,
      vehicleModel: data?.vehicleModel,
      vehicleTitleStatus: data?.vehicleTitleStatus,
      catSerialNumber: data?.catSerialNumber,
      catOemCode: data?.catOemCode,
      catComplianceStatus: data?.catComplianceStatus,
      parentLineItemId: data?.parentLineItemId,
    };

    const updated = { ...state, items: [...state.items, newItem] };
    set($itemsInterfaceEntries, updated);
    set($newlyAddedItemId, newId);
    notifyCallback(get, updated);
  },
);

// 🟠🟠🟠 ACTION -> UPDATE LINE ITEM
export const $updateLineItem = atom(
  null,
  (get, set, id: string, updates: Partial<NormalizedLineItem>) => {
    const state = get($itemsInterfaceEntries);

    const updatedItems = state.items.map((entry) =>
      entry.id === id && entry.kind === enumLineItemKind.LineItem
        ? { ...entry, ...updates }
        : entry,
    );
    const updated = { ...state, items: updatedItems };
    set($itemsInterfaceEntries, updated);
    notifyCallback(get, updated);
  },
);

// 🟠🟠🟠 ACTION -> UPDATE LINE ITEM TAG
export type UpdateLineItemTagInput = {
  lineItemId: string;
  tagId: string;
  itemId: string;
  grossQuantity: number;
  tare: number;
  netQuantity: number;
  effectivePricingUnit?: string;
};

export const $updateLineItemTag = atom(
  null,
  (get, set, input: UpdateLineItemTagInput) => {
    const state = get($itemsInterfaceEntries);
    const suggestedPrices = get($itemsInterfaceSuggestedPrices);
    const unit = (input.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
    const suggested = resolveSuggestedPrice(suggestedPrices, input.itemId);

    const updatedItems = state.items.map((entry) =>
      entry.id === input.lineItemId && entry.kind === enumLineItemKind.LineItem
        ? {
            ...entry,
            tagId: input.tagId,
            itemId: input.itemId,
            weights: {
              [enumLoadWeightType.Gross]: {
                ...zeroWeight(unit),
                value: input.grossQuantity ?? 0,
              },
              [enumLoadWeightType.Tare]: {
                ...zeroWeight(unit),
                value: input.tare ?? 0,
              },
              [enumLoadWeightType.Net]: {
                ...zeroWeight(unit),
                value: input.netQuantity ?? 0,
              },
            },
            unitPrice: suggested?.unitPrice ?? null,
            pricingUnit: suggested?.pricingUnit ?? unit,
          }
        : entry,
    );
    const updated = { ...state, items: updatedItems };
    set($itemsInterfaceEntries, updated);
    notifyCallback(get, updated);
  },
);

// 🟠🟠🟠 ACTION -> DELETE ENTRY
export const $deleteEntry = atom(null, (get, set, id: string) => {
  const state = get($itemsInterfaceEntries);

  const deletedEntry = state.items.find((entry) => entry.id === id);
  const updatedItems = state.items.filter((entry) => entry.id !== id);
  const updated = { ...state, items: updatedItems };
  set($itemsInterfaceEntries, updated);

  if (
    deletedEntry?.kind === enumLineItemKind.Deduction &&
    get($deductionChangeCallback)
  ) {
    notifyDeductionChange(
      get,
      'delete',
      deletedEntry as NormalizedDeduction,
      updated.items,
    );
  } else {
    notifyCallback(get, updated);
  }
});

// 🟠🟠🟠 ACTION -> ADD DEDUCTION
export const $addDeduction = atom(
  null,
  (
    get,
    set,
    deduction: {
      typeId: string;
      typeName: string;
      amount: number;
      isWeightBased: boolean;
      lineItemId?: string;
    },
  ) => {
    const state = get($itemsInterfaceEntries);
    const direction = get($ticketDirection);

    const newId = `temp-${uuidv4()}`;
    const lineItemId = deduction.lineItemId ?? null;
    const newEntry: NormalizedDeduction = {
      kind: enumLineItemKind.Deduction,
      id: newId,
      deductionTypeId: deduction.typeId,
      deductionTypeName: deduction.typeName,
      applicationType: deduction.isWeightBased ? 'Weight' : 'Price',
      weightValue: deduction.isWeightBased
        ? toWeight(deduction.amount, enumUnit.Pounds)
        : null,
      priceValue: !deduction.isWeightBased
        ? toMoney({
            amount: deduction.amount,
            currencyCode: enumCurrencyCode.USD,
          })
        : null,
      calculatedAmount: null,
      pricingUnit: enumUnit.Pounds,
      percentage: null,
      purchaseLoadLineItemId: direction === 'inbound' ? lineItemId : null,
      loadLineItemId: direction === 'outbound' ? lineItemId : null,
    };

    const updated = { ...state, items: [...state.items, newEntry] };
    set($itemsInterfaceEntries, updated);
    set($newlyAddedItemId, newId);

    if (get($deductionChangeCallback)) {
      notifyDeductionChange(get, 'create', newEntry, updated.items);
    } else {
      notifyCallback(get, updated);
    }
  },
);

// 🟢🟢🟢 HELPER -> get deductions for a specific line item
export const getLineItemDeductions = (
  items: ItemsListEntry[],
  lineItemId: string | null,
): NormalizedDeduction[] => {
  if (!lineItemId) return [];
  return items.filter(
    (e): e is NormalizedDeduction =>
      e.kind === enumLineItemKind.Deduction &&
      (e.purchaseLoadLineItemId === lineItemId ||
        e.loadLineItemId === lineItemId),
  );
};

// 🟢🟢🟢 HELPER -> get line item by id
export const getLineItemById = (
  items: ItemsListEntry[],
  lineItemId: string | null,
): NormalizedLineItem | undefined => {
  if (!lineItemId) return undefined;
  return items.find(
    (e): e is NormalizedLineItem =>
      e.kind === enumLineItemKind.LineItem && e.id === lineItemId,
  );
};

// 🟠🟠🟠 ACTION -> UPDATE DEDUCTION
export const $updateDeduction = atom(
  null,
  (get, set, id: string, updates: Partial<NormalizedDeduction>) => {
    const state = get($itemsInterfaceEntries);

    const updatedItems = state.items.map((entry) =>
      entry.id === id && entry.kind === enumLineItemKind.Deduction
        ? { ...entry, ...updates }
        : entry,
    );
    const updated = { ...state, items: updatedItems };
    set($itemsInterfaceEntries, updated);

    const updatedDeduction = updated.items.find(
      (e) => e.id === id && e.kind === enumLineItemKind.Deduction,
    ) as NormalizedDeduction | undefined;

    if (updatedDeduction && get($deductionChangeCallback)) {
      notifyDeductionChange(get, 'update', updatedDeduction, updated.items);
    } else {
      notifyCallback(get, updated);
    }
  },
);

// 🟠🟠🟠 ACTION -> ADD FREIGHT CHARGE
export const $addFreightCharge = atom(null, (get, set) => {
  const state = get($itemsInterfaceEntries);

  const hasFreight = state.items.some(
    (e) => e.kind === enumLineItemKind.FreightCharge,
  );
  if (hasFreight) return;

  const newId = `temp-${uuidv4()}`;
  const newEntry: NormalizedFreightCharge = {
    kind: enumLineItemKind.FreightCharge,
    id: newId,
    freightCost: null,
    notes: null,
  };

  const updated = { ...state, items: [...state.items, newEntry] };
  set($itemsInterfaceEntries, updated);
  set($newlyAddedItemId, newId);
});

// 🟠🟠🟠 ACTION -> UPDATE FREIGHT CHARGE
export const $updateFreightCharge = atom(
  null,
  (get, set, id: string, updates: Partial<NormalizedFreightCharge>) => {
    const state = get($itemsInterfaceEntries);

    const updatedItems = state.items.map((entry) =>
      entry.id === id && entry.kind === enumLineItemKind.FreightCharge
        ? { ...entry, ...updates }
        : entry,
    );
    const updated = { ...state, items: updatedItems };
    set($itemsInterfaceEntries, updated);
    notifyCallback(get, updated);
  },
);

const computeNominalPrice = (item: NormalizedLineItem): number => {
  const isEach = item.pricingUnit === enumUnit.Each;
  const unitPrice = item.unitPrice?.amount ?? 0;
  if (isEach) {
    const netWeight = item.weights?.[enumLoadWeightType.Net];
    return unitPrice * (netWeight?.value ?? 0);
  }
  const grossWeight = item.weights?.[enumLoadWeightType.Gross];
  const tareWeight = item.weights?.[enumLoadWeightType.Tare];
  const grossLbs = grossWeight ? weightToPounds(grossWeight) : 0;
  const tareLbs = tareWeight ? weightToPounds(tareWeight) : 0;
  const netLbs = grossLbs - tareLbs;
  return unitPrice * convertWeightForPricing(netLbs, item.pricingUnit);
};

// 🟢🟢🟢 COMPUTED DATA -> monetary totals (deductions from server, subtotal/freight from entries)
export const $monetaryTotals = atom((get) => {
  const { items } = get($itemsInterfaceEntries);
  const deductionsTotal = get($deductionsTotalFromServer);

  const lineItems = items.filter(
    (e): e is NormalizedLineItem => e.kind === enumLineItemKind.LineItem,
  );
  const freightCharges = items.filter(
    (e): e is NormalizedFreightCharge =>
      e.kind === enumLineItemKind.FreightCharge,
  );

  const subtotal = lineItems.reduce(
    (sum, item) => sum + computeNominalPrice(item),
    0,
  );
  const freightTotal = freightCharges.reduce(
    (sum, fc) => sum + (fc.freightCost?.amount ?? 0),
    0,
  );
  const currencyCode =
    lineItems.find((item) => item.unitPrice?.currencyCode != null)?.unitPrice
      ?.currencyCode ?? enumCurrencyCode.USD;

  return {
    subtotal,
    deductionsTotal,
    freightTotal,
    purchaseTotal: subtotal - deductionsTotal + freightTotal,
    currencyCode,
  };
});

// 🟠🟠🟠 ACTION -> recalculate deductions total from current entries
export const $recalculateDeductionsTotal = atom(null, (get, set) => {
  const { items } = get($itemsInterfaceEntries);
  const deductions = items.filter(
    (e): e is NormalizedDeduction => e.kind === enumLineItemKind.Deduction,
  );
  const total = deductions.reduce(
    (sum, d) => sum + (d.calculatedAmount?.amount ?? 0),
    0,
  );
  set($deductionsTotalFromServer, total);
});

// 🟢🟢🟢 COMPUTED DATA -> purchase total (backward-compatible alias)
export const $purchaseTotal = atom<{
  amount: number;
  currencyCode: CurrencyCode;
}>((get) => {
  const totals = get($monetaryTotals);
  return { amount: totals.purchaseTotal, currencyCode: totals.currencyCode };
});

// 🟢🟢🟢 COMPUTED DATA -> purchase summary breakdown (backward-compatible alias)
export const $purchaseSummary = atom((get) => {
  const totals = get($monetaryTotals);
  return {
    subtotal: { amount: totals.subtotal, currencyCode: totals.currencyCode },
    deductionsTotal: {
      amount: totals.deductionsTotal,
      currencyCode: totals.currencyCode,
    },
    grandTotal: {
      amount: totals.purchaseTotal,
      currencyCode: totals.currencyCode,
    },
  };
});
